import { useLocation } from "wouter";

// Components
import { SuperAdminLayout } from "@/components/admin/SuperAdminLayout";
import { BackToPageButton } from "@/components/jobs/BackToPageButton";
import { JobForm } from "@/components/jobs/JobForm";

// Hooks & Contexts
import { useToast } from "@/hooks/use-toast";

// API & Types
import { createJob } from "@/api/jobs";

// Utils
import { JobFormState, formStateToJobData, validateJobForm } from "@/lib/jobFormUtils";

export default function CreateJob() {
  // Hooks
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  // Handle job creation
  const handleCreateJob = async (formData: JobFormState, recruiters?: Array<{id: string, firstName: string, lastName: string}>): Promise<boolean> => {
    try {
      // Validate form data before processing (isNewJob = true for creation)
      if (!validateJobForm(formData, true)) {
        // TODO: Integrate with Sentry for error tracking and reporting
        toast({
          title: "Invalid Job Data",
          description: "Please fill in all required fields before creating the job.",
          variant: "destructive"
        });
        return false;
      }

      const jobData = formStateToJobData(formData, recruiters);
      
      // Add additional fields required for new job creation
      // These fields are excluded from insertJobSchema but need to be set
      const newJobData = {
        ...jobData,
        recruiterId: formData.assignedRecruiter || "unassigned",
        // Initialize auto-calculated counters (excluded from insertJobSchema)
        currentHires: 0,
        referralCount: 0,
        viewCount: 0,
        applicants: 0,
        totalEarnings: 0,
        avgMatchScore: 0,
        conversionRate: 0,
        // Set default values for required fields
        targetHires: jobData.targetHires || 1,
        status: jobData.status || "draft",
        priority: jobData.priority || "normal",
        // Ensure reward amount is set (required field)
        rewardInAirPoints: jobData.rewardInAirPoints || 0
      };

      await createJob(newJobData);

      toast({
        title: "Job Created Successfully",
        description: `Job "${formData.title}" has been created and is ready for referrals.`
      });

      return true;
    } catch (error) {
      console.error("Error creating job:", error);
      
      let errorMessage = "Failed to create job. Please try again.";
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Error Creating Job",
        description: errorMessage,
        variant: "destructive"
      });
      
      return false;
    }
  };

  const handleCancel = () => {
    // Could navigate back or reset form
  };

  return (
    <SuperAdminLayout 
      title="Create New Job"
      subtitle="Add a new job posting to the platform"
      maxWidth="4xl"
    >
      <div className="space-y-6" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        <div className="flex items-center gap-4">
          <BackToPageButton 
            onClick={() => setLocation("/admin/jobs")}
            text="Back to Jobs"
          />
        </div>

        <JobForm
          onSubmit={handleCreateJob}
          onCancel={handleCancel}
          submitButtonText="Create Job"
          title="Job Details"
        />
      </div>
    </SuperAdminLayout>
  );
}